import type {
  QueryRoot,
  Blog as IBlog,
  Product,
} from "@shopify/hydrogen/storefront-api-types";
import {
  ProductGrid,
  ProductType,
} from "@components/organisms/product-grid/index.client";
import SolidButton from "@components/atoms/base-button/SolidButton";
import { BlogListGrid } from "@components/organisms/blog-list";
import { CloseButton } from "@components/atoms/close-button";
import { SearchIcon } from "@components/atoms/Icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { Field, Form, Formik } from "formik";
import { Link } from "@shopify/hydrogen";
import { translate } from "@locales";
import classNames from "classnames";

function useSearchHeader() {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [blogs, setBlogs] = useState<IBlog[]>([]);
  const show = useMemo(() => Boolean(query), [query]);

  const initialValues = {
    query: "",
  };

  const submitHandler = (values: typeof initialValues) => {
    setQuery(values.query);
    fetch(`/api/search?query=${values.query}`)
      .then((response) => response.json())
      .then((data: Record<string, { data: QueryRoot }>) => {
        setProducts(data.products.data.products.nodes ?? []);
        setBlogs(data.blogs.data.blogs.nodes ?? []);
      })
      .catch(console.error);
  };

  useEffect(() => {
    const body = document.body;
    body.style.overflow = show ? "hidden" : "auto";
  }, [show]);

  return {
    models: {
      initialValues,
      products,
      blogs,
      closeSearch: () => {},
      query,
      show,
    },
    operations: {
      submitHandler,
    },
  };
}

export function SearchHeader({ closeSearch }: { closeSearch: () => void }) {
  const { operations, models } = useSearchHeader();
  const { products, query, show, blogs, initialValues } = models;
  const { submitHandler } = operations;

  const headerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const isInsideHeader = headerRef.current?.contains(target);
      const isInsideOverlay = overlayRef.current?.contains(target);
      if (!isInsideHeader && !isInsideOverlay) {
        closeSearch();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <header
        className={classNames(
          "bg-white w-full h-[100px] max-w-full px-4 sm:px-6 max-auto"
        )}
      >
        <div
          ref={headerRef}
          className={classNames(
            "flex h-full items-center justify-between border-b-2 border-gray-100 lg:justify-start lg:space-x-10",
            { "border-b-0": show }
          )}
        >
          <Formik initialValues={initialValues} onSubmit={submitHandler}>
            {({ values, setFieldValue }) => (
              <div className="w-full flex justify-center">
                <Form className="relative flex justify-center items-center lg:w-4/5 w-full">
                  <CloseButton onClose={closeSearch} />
                  <Field
                    placeholder={translate("search.input.placeholder")}
                    onKeyUp={(e: any) => {
                      setFieldValue("query", e.target.value);
                      submitHandler(values);
                    }}
                    name="query"
                    autoFocus
                    className="bg-custom-light-gray w-full h-10 p-4 outline-none"
                  />
                  <button
                    className="h-10 w-10 bg-custom-light-gray flex items-center justify-center"
                    type="submit"
                  >
                    <SearchIcon width="16" height="16" />
                  </button>
                  <CloseButton
                    onClose={closeSearch}
                    className="hidden lg:!flex"
                  />
                </Form>
              </div>
            )}
          </Formik>
        </div>
      </header>

      <div className="flex-1 bg-black/40">
        <div
          ref={overlayRef}
          className={classNames(
            "h-[calc(100vh-100px)] overflow-auto bg-white w-full border-t-2 border-gray-100 flex justify-center shadow-gray-900/40 shadow-md",
            { hidden: !show }
          )}
        >
          <div className="lg:w-4/5 px-5 w-full lg:px-4 sm:px-6">
            <ProductGrid
              products={products}
              productType={ProductType.ViewOnly}
              className="hidden lg:flex gap-11 my-10 mx-0"
              onRedirect={closeSearch}
            />
            <ProductGrid
              products={products}
              productType={ProductType.Mobile}
              className="justify-center m-0 my-8 lg:hidden"
              onRedirect={closeSearch}
            />
            <SearchBlogsRender
              query={query}
              blogs={blogs}
              closeSearch={closeSearch}
            />
            <Link
              to={`/search?query=${query}`}
              onClick={closeSearch}
              className="flex justify-center pb-16"
            >
              <SolidButton className="w-[200px]">
                {translate("base.view-more")}
              </SolidButton>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function SearchBlogsRender({
  query,
  blogs,
  closeSearch,
}: Pick<
  ReturnType<typeof useSearchHeader>["models"],
  "query" | "blogs" | "closeSearch"
>) {
  const noResult = (
    <div className="flex h-40 justify-center items-center mt-4">
      <p className="text-center lowercase text-dark-gray">
        {`${translate("search.blog.no-result")} "${query}"`}
      </p>
    </div>
  );

  return (
    <>
      <h1 className="text-xs lg:text-lg">{translate("search.blog.title")}</h1>
      <div className="w-full border-t-2 border-gray-100">
        {blogs.length ? (
          <BlogListGrid closeSearch={closeSearch} blogs={blogs} />
        ) : (
          noResult
        )}
      </div>
    </>
  );
}
